<script setup>
  import {computed, inject, onBeforeMount, ref, watch} from "vue";

  import ComponentDosageInput from "@components/Inputs/DosageInput.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {medicationMeal, medicationUsage} from "@enums/medicationEnums.js";
  import {useForm} from "@inertiajs/vue3";
  import dosageModel from "@models/dosageModel.js";
  import MedicationComponentIndicationDialog from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationDialog.vue";
  import MedicationComponentIndicationDisplay from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationDisplay.vue";
  import * as dosageHelper from "@pages/Records/Helper/dosageHelper.js";
  import {MedButton, MedIcon, MedTooltip} from "@pharma4u/medicheck-components";
  import {cloneDeep} from "es-toolkit";

  defineOptions({
    name: "MedicationComponentDetailDialogMedicationData",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    medicationForm: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["invalid", "valid", "save", "changeForm", "resetIndications"]);

  const openDialog = inject("medicationOpenDialog");

  const refIndicationMenuDialog = ref(null);

  const dosageForm = useForm({
    value: null,
  });

  const currentDosageUnit = computed(() => dosageHelper.getCurrentDosageUnit(dosageForm.value));

  const currentDosageUnitList = computed(() => dosageHelper.getDosageUnitList(props.medicationForm.value.core));

  watch(
    () => props.medicationForm.value.dosage,
    () => {
      dosageForm.value = cloneDeep(props.medicationForm.value.dosage);
    },
    {deep: true},
  );

  onBeforeMount(() => {
    dosageForm.value = cloneDeep(dosageModel);
    Object.assign(dosageForm.value, props.medicationForm.value.dosage);
  });

  const handleInvalidDosage = (dosageId) => {
    emit("invalid", dosageId);
  };

  const updateDosage = (newValues, type) => {
    const asFraction = type + "_as_fraction";

    dosageHelper.updateDosage(dosageForm.value, type, newValues, () => {
      emit("changeForm", {key: "dosage." + type, newValue: dosageForm.value[type]});
      emit("changeForm", {key: "dosage." + asFraction, newValue: dosageForm.value[asFraction]});

      emit("valid", newValues.id);
    });
  };

  const updateUsage = (newValue) => {
    emit("changeForm", {key: "usage", newValue: newValue});
  };

  const updateDosageUnit = ({keyDoe, unit}) => {
    dosageHelper.updateDosageUnit(dosageForm.value, keyDoe, unit, () => {
      emit("changeForm", {key: "dosage.selected_unit", newValue: dosageForm.value.selected_unit});
      emit("changeForm", {key: "dosage.selected_unit_key_doe", newValue: dosageForm.value.selected_unit_key_doe});
    });
  };

  const updateMeal = () => {
    emit("changeForm", {key: "dosage.meal", newValue: dosageForm.value.meal});
  };

  const openIndicationDialog = () => {
    if (props.isEditable) {
      refIndicationMenuDialog.value.open({
        medication: props.medicationForm.value,
        medicationKeyFam: props.medicationForm.value.abdata_key_fam,
        medicationHashId: props.medicationForm.value.hashId,
      });
    }
  };
</script>

<template>
  <div>
    <!-- fam name / redhand / fi button -->
    <div class="flex items-center space-x-2">
      <span
        v-if="medicationForm.value.abdata_key_fam === null || medicationForm.value.abdata_key_fam === 0"
        class="text-lg font-medium text-mcred"
      >
        **
      </span>

      <span class="text-lg font-medium hyphens-auto" :data-test="getTestId('fam')">{{ medicationForm.value.fam }}</span>

      <!-- fi button -->
      <div v-if="medicationForm.value.core.redhand" class="relative">
        <MedTooltip content="Rote-Hand-Brief (ggf. weitere Dokumente)">
          <MedButton
            aria-label="Fachinformationen und Rote-Hand-Brief anzeigen"
            rounded="full"
            size="xs"
            class="size-7.5 shrink-0 bg-gray-200 p-1 text-sm hover:bg-gray-300"
            :data-test="getTestId('fi-button')"
            @click="openDialog('refCoreInfoDialog', medicationForm.value)"
          >
            FI
          </MedButton>

          <MedIcon size="xs" icon="pan_tool" class="absolute -top-1 -right-1 text-sm text-mcred select-none" />
        </MedTooltip>
      </div>

      <div v-else class="ml-2">
        <MedTooltip content="Fachinformation (ggf. weitere Dokumente)">
          <MedButton
            aria-label="Fachinformationen anzeigen"
            rounded="full"
            size="xs"
            class="size-7.5 shrink-0 bg-gray-200 p-1 text-sm hover:bg-gray-300"
            :data-test="getTestId('open-core-dialog-button')"
            @click="openDialog('refCoreInfoDialog', medicationForm.value)"
          >
            FI
          </MedButton>
        </MedTooltip>
      </div>
    </div>

    <!-- substances / form / manufacturer / pzn --->
    <div class="mt-2 text-xs text-gray-500">
      <template v-if="medicationForm.value.core.substancesText && medicationForm.value.core.substancesText.length > 0">
        {{ medicationForm.value.core.substancesText + " / " ?? "-" + " / " }}
      </template>

      <template v-if="medicationForm.value.form && medicationForm.value.form.length > 0">
        {{ medicationForm.value.form + " / " }}
      </template>

      <template v-if="medicationForm.value.core.manufactureText">
        {{ medicationForm.value.core.manufactureText + " / " }}
      </template>

      <span v-if="medicationForm.value.core.selectedPackageLabel === 'außer Vertrieb'">
        {{ medicationForm.value.core.selectedPackageLabel }}
      </span>

      <span v-else>PZN: {{ medicationForm.value.selected_package_pzn }}</span>

      <template v-if="medicationForm.value.core.package">{{ " / " + medicationForm.value.core.package }}</template>
    </div>

    <div class="mt-4 grid grid-cols-12 gap-x-2 gap-y-4">
      <!-- medication -->
      <div class="col-span-4 md:col-span-2">
        <ComponentSelect
          :id="'usage' + medicationForm.value.id"
          ref="usage"
          label="Art"
          :name="'usage' + medicationForm.value.id"
          :disabled="!isEditable"
          :model-value="medicationForm.value.usage"
          :nullable="true"
          :options="medicationUsage"
          :test-id="getTestId('usage')"
          @change="updateUsage"
        />
      </div>
      <div class="col-span-4 m-auto md:col-span-2">
        <p class="mb-1 ml-2 text-sm text-gray-500">Dosierung</p>

        <div class="flex gap-1">
          <ComponentDosageInput
            :id="'medicationDataDosageMorning' + medicationForm.value.id"
            :value="dosageForm.value.morning"
            :name="'medicationDataDosageMorning' + medicationForm.value.id"
            :as-fraction="dosageForm.value.morning_as_fraction"
            :disabled="!isEditable || !!dosageForm?.value?.dosagealternative?.type"
            :test-id="getTestId('morning')"
            @input="updateDosage($event, 'morning')"
            @is-invalid="handleInvalidDosage"
          />

          <ComponentDosageInput
            :id="'medicationDataDosageNoon' + medicationForm.value.id"
            :value="dosageForm.value.noon"
            :name="'medicationDataDosageNoon' + medicationForm.value.id"
            :as-fraction="dosageForm.value.noon_as_fraction"
            :disabled="!isEditable || !!dosageForm?.value?.dosagealternative?.type"
            :test-id="getTestId('noon')"
            @input="updateDosage($event, 'noon')"
            @is-invalid="handleInvalidDosage"
          />

          <ComponentDosageInput
            :id="'medicationDataDosageEvening' + medicationForm.value.id"
            :value="dosageForm.value.evening"
            :name="'medicationDataDosageEvening' + medicationForm.value.id"
            :as-fraction="dosageForm.value.evening_as_fraction"
            :disabled="!isEditable || !!dosageForm?.value?.dosagealternative?.type"
            :test-id="getTestId('evening')"
            @input="updateDosage($event, 'evening')"
            @is-invalid="handleInvalidDosage"
          />

          <ComponentDosageInput
            :id="'medicationDataDosageNight' + medicationForm.value.id"
            :value="dosageForm.value.night"
            :name="'medicationDataDosageNight' + medicationForm.value.id"
            :as-fraction="dosageForm.value.night_as_fraction"
            :disabled="!isEditable || !!dosageForm?.value?.dosagealternative?.type"
            :test-id="getTestId('night')"
            @input="updateDosage($event, 'night')"
            @is-invalid="handleInvalidDosage"
          />
        </div>
      </div>

      <div class="col-span-4 md:col-span-2">
        <ComponentSelect
          :id="'dosageUnit' + medicationForm.value.id"
          ref="unitfield"
          :model-value="currentDosageUnit"
          label="Einheit"
          :data-hash-id="medicationForm.value.id"
          :disabled="!isEditable"
          :options="currentDosageUnitList"
          key-value="keyDoe"
          key-name="unit"
          return-type="object"
          :test-id="getTestId('unit')"
          @change="updateDosageUnit"
        />
      </div>

      <div class="col-span-6 md:col-span-3">
        <ComponentSelect
          :id="'meal' + medicationForm.value.id"
          ref="meal"
          v-model="dosageForm.value.meal"
          :name="'meal' + medicationForm.value.id"
          :disabled="!isEditable"
          label="Mahlzeit"
          key-name="name"
          :nullable="true"
          :options="medicationMeal"
          :test-id="getTestId('meal')"
          @change="updateMeal"
        />
      </div>

      <div class="col-span-6 md:col-span-3">
        <MedicationComponentIndicationDisplay
          show-label
          :indications="medicationForm.value.indications"
          :indications-unknown="medicationForm.value.indication_unknown"
          :is-active="isEditable"
          :disabled="!isEditable"
          :is-discontinued="!!medicationForm.value.additionalinformation?.discontinued"
          @open-dialog="openIndicationDialog"
        />

        <template v-if="isEditable">
          <MedicationComponentIndicationDialog
            ref="refIndicationMenuDialog"
            :is-detail-dialog="true"
            @form-change="$emit('changeForm', $event)"
            @close="$emit('resetIndications')"
          />
        </template>
      </div>
      <!-- medication end -->
    </div>
  </div>
</template>
